import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Table } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'

import { personsGetActive, salaryHistoryGetAll } from '../../api'
import { DashboardIconChanges } from '../../common/icons'
import { LoadingContainer } from '../../common/loaders/loadingcontainer'
import { localized, numberFormatterFraction } from '../../common/utils'

export function ChangedSalaryInformation() {
	const { t } = useTranslation()

	const startKey = 'changedSalary_startDate'
	const endKey = 'changedSalary_endDate'

	const [startDate, setStartDate] = useState(() => {
		return (
			localStorage.getItem(startKey) ||
			new Date(Date.UTC(new Date().getFullYear(), new Date().getMonth(), 1)).toISOString().split('T')[0]
		)
	})

	const [endDate, setEndDate] = useState(() => {
		return (
			localStorage.getItem(endKey) ||
			new Date(Date.UTC(new Date().getFullYear(), new Date().getMonth() + 1, 0)).toISOString().split('T')[0]
		)
	})

	const { data: salaryRes, isLoading: salaryLoading } = useQuery({
		queryKey: ['salaryHistoryAll'],
		queryFn: salaryHistoryGetAll,
	})

	const { data: personsRes, isLoading: personsLoading } = useQuery({
		queryKey: ['personsActive'],
		queryFn: personsGetActive,
	})

	const [formattedData, setFormattedData] = useState([])

	useEffect(() => {
		if (salaryRes?.data && personsRes?.data) {
			const personsMap = new Map(personsRes.data.map((p) => [p.personId, p]))

			const rows = salaryRes.data
				.map((item) => {
					const validFromDate = item.salaryValidFrom
					if (!validFromDate) return null

					const person = personsMap.get(item.personId)
					const personName = person
						? person.name || `${person.forename} ${person.surname}`
						: `${t('Person', 'Henkilö')} ${item.personId}`

					return {
						...item,
						rowId: Math.random(),
						personName,
						resolvedValidFrom: validFromDate,
					}
				})
				.filter(Boolean)

			setFormattedData(rows)
		}
	}, [salaryRes, personsRes, t])

	const handleStartDateChange = (val) => {
		setStartDate(val)
		localStorage.setItem(startKey, val)
	}

	const handleEndDateChange = (val) => {
		setEndDate(val)
		localStorage.setItem(endKey, val)
	}

	const filteredData = formattedData.filter((item) => {
		if (!item.resolvedValidFrom) return false

		const validFromDate = new Date(item.resolvedValidFrom)

		if (startDate) {
			const start = new Date(startDate)
			if (validFromDate < start) return false
		}

		if (endDate) {
			const end = new Date(endDate)
			end.setHours(23, 59, 59, 999)
			if (validFromDate > end) return false
		}

		return true
	})

	if (salaryLoading || personsLoading) {
		return <LoadingContainer />
	}

	return (
		<div>
			<div
				className="mb-3"
				style={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
				}}
			>
				<div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
					<span className="starting-employments-icon">
						<DashboardIconChanges />
					</span>
					<h4 style={{ margin: 0, color: 'var(--heeros-blue)' }}>
						{t('ChangedSalaryInformation', 'Muuttuneet palkkatiedot')}
					</h4>
				</div>

				<div style={{ display: 'flex', gap: '10px' }}>
					<input
						type="date"
						className="form-control form-control-sm"
						style={{ width: 'auto' }}
						value={startDate}
						onChange={(e) => handleStartDateChange(e.target.value)}
					/>
					<input
						type="date"
						className="form-control form-control-sm"
						style={{ width: 'auto' }}
						value={endDate}
						onChange={(e) => handleEndDateChange(e.target.value)}
					/>
				</div>
			</div>

			<div style={{ width: '100%', overflowX: 'auto', maxHeight: '400px' }}>
				<Table striped bordered hover responsive size="sm" style={{ fontSize: '0.9rem', margin: 0 }}>
					<thead
						style={{
							position: 'sticky',
							top: 0,
							backgroundColor: '#f8f9fa',
							zIndex: 1,
						}}
					>
						<tr>
							<th>{t('Person', 'Henkilö')}</th>
							<th>{t('ValidFrom', 'Voimassaolon alkupvm.')}</th>
							<th>{t('EmploymentType', 'Palvelussuhteen tyyppi')}</th>
							<th className="text-end">{t('PartTimePercentage', 'Osa-aikaprosentti')}</th>
						</tr>
					</thead>
					<tbody>
						{filteredData.length > 0 ? (
							filteredData.map((row) => {
								const partTimeVal = numberFormatterFraction
									? numberFormatterFraction({ value: row.partTimePercentage })
									: row.partTimePercentage

								return (
									<tr key={row.rowId}>
										<td>{row.personName}</td>
										<td>
											{row.resolvedValidFrom
												? new Date(row.resolvedValidFrom).toLocaleDateString(localized.resolvedLanguage)
												: '-'}
										</td>
										<td>{localized?.employmentTypes?.[row.employmentType] || row.employmentType || '-'}</td>
										<td className="text-end">{partTimeVal ? `${partTimeVal} %` : '0 %'}</td>
									</tr>
								)
							})
						) : (
							<tr>
								<td colSpan={4} className="text-center text-muted py-3">
									{t('NoDataFound', 'Ei tietoja valitulla aikavälillä')}
								</td>
							</tr>
						)}
					</tbody>
				</Table>
			</div>
		</div>
	)
}
